import React, { useState, useMemo, useCallback } from "react";
import {
  Modal,
  Button,
  Radio,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Alert,
} from "antd";
import { useAuth } from "../context/AuthContext";
import {
  PLAN_TIERS,
  PLAN_NAMES,
  PRICING_DATA,
  calculatePricing,
  formatCurrency,
} from "../constants/PricingConstants";
import { SUBSCRIPTION_CONFIG } from "../config/zibuzzConfig";
import { handleSubscriptionFlow } from "../utils/subscriptionUtils";
import styles from "./SubscriptionModal.module.css";

const { Text, Title } = Typography;

/* ─── Plan & Validity Constants ─── */

const PAID_PLANS = Object.values(PRICING_DATA)
  .sort((a, b) => (a.planHierarchy || 0) - (b.planHierarchy || 0))
  .map((p) => p.tier);

const VALIDITY = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  ANNUAL: "annual",
};

const VALIDITY_OPTIONS = [
  { value: VALIDITY.MONTHLY, label: "Monthly", discount: 0 },
  { value: VALIDITY.QUARTERLY, label: "Quarterly (2% off)", discount: 2 },
  { value: VALIDITY.ANNUAL, label: "Annual (5% off)", discount: 5 },
];

const formatPlanLabel = (plan) =>
  plan ? PLAN_NAMES[plan] || plan.charAt(0).toUpperCase() + plan.slice(1) : "—";

const formatExpiry = (expiry) => {
  if (!expiry) return "—";
  try {
    return new Date(expiry).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return expiry;
  }
};

/* ─── Component ─── */

export default function SubscriptionModal({ open, onCancel, userProfile, initialPlan, initialValidity }) {
  const { token, setToken } = useAuth();

  /* Derive current plan from token */
  const planData = useMemo(() => {
    const p = userProfile?.Plan || {};
    const plan = (p.plan || p.Plan || null)?.toLowerCase();
    const expiry = p.expiry || p.Expiry || "";
    const trialUsed = userProfile?.TrialUsed || plan === "trial";

    return {
      plan,
      expiry,
      trialUsed,
      teamsize: p.teamsize || p.TeamSize || 1,
    };
  }, [userProfile]);

  const isTrialActive = planData.plan === "trial";

  // Check if within renewal window (expiry within configured days)
  const isWithinRenewalWindow = useMemo(() => {
    if (!planData.expiry || isTrialActive) return true;
    try {
      const expiryDate = new Date(planData.expiry);
      const today = new Date();
      const daysUntilExpiry = Math.ceil(
        (expiryDate - today) / (1000 * 60 * 60 * 24)
      );
      return daysUntilExpiry <= SUBSCRIPTION_CONFIG.ALLOW_RENEWAL_WINDOW_DAYS;
    } catch {
      return false;
    }
  }, [planData.expiry, isTrialActive]);

  /* ─── View state ─── */
  // If initialPlan is provided, skip overview and go directly to renew view
  const [view, setView] = useState(() => initialPlan ? "renew" : "overview"); // 'overview' | 'renew'
  const [actionLoading, setActionLoading] = useState(false);

  /* ─── Renew state ─── */
  const [renewPlan, setRenewPlan] = useState(() =>
    initialPlan || (isTrialActive ? SUBSCRIPTION_CONFIG.DEFAULT_PLAN_WHEN_TRIAL : planData.plan)
  );
  const [renewValidity, setRenewValidity] = useState(() =>
    initialValidity || VALIDITY.MONTHLY
  );

  /* Reset state when modal opens/closes */
  const handleCancel = useCallback(() => {
    setView(initialPlan ? "renew" : "overview");
    setActionLoading(false);
    onCancel?.();
  }, [onCancel, initialPlan]);

  const handleOpenRenew = useCallback(() => {
    setRenewPlan(
      isTrialActive ? SUBSCRIPTION_CONFIG.DEFAULT_PLAN_WHEN_TRIAL : planData.plan
    );
    setRenewValidity(VALIDITY.MONTHLY);
    setView("renew");
  }, [isTrialActive, planData.plan]);

  /* ─── Renew / Subscribe ─── */
  const handleRenewConfirm = useCallback(async () => {
    if (!renewPlan) {
      return;
    }
    if (!token) {
      return;
    }

    setActionLoading(true);

    await handleSubscriptionFlow({
      token,
      targetPlan: renewPlan,
      validity: renewValidity,
      userProfile,
      setToken,
      onSuccess: () => {
        setActionLoading(false);
        handleCancel();
      },
      onError: (error) => {
        setActionLoading(false);
        console.error("Subscription flow error:", error);
      },
      onAdjustmentInfo: (adjustInfo) => {
        // Show adjustment info as toast message with longer duration
        message.info({
          content: adjustInfo.message,
          duration: 8, // 8 seconds (default is 3)
          style: {
            marginTop: '20vh',
          },
        });
      },
    });
  }, [token, renewPlan, renewValidity, userProfile, setToken, handleCancel]);

  const isBusy = actionLoading;

  /* ─── Calculate pricing for selected plan and validity ─── */
  const currentPricing = useMemo(() => {
    if (!renewPlan || !renewValidity) return null;
    return calculatePricing(renewPlan, renewValidity);
  }, [renewPlan, renewValidity]);

  /* ─── Render helpers ─── */

  const overviewContent = (
    <div>
      {/* Renewal Alert (show if within renewal window and not trial) */}
      {!isTrialActive && isWithinRenewalWindow && (
        <Alert
          message={SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.planExpiringSoon}
          description={`Your plan expires on ${formatExpiry(
            planData.expiry
          )}. Renew now to avoid interruption.`}
          type="warning"
          showIcon
          className={styles.renewalAlert}
        />
      )}

      {/* Current Plan Card */}
      <div className={styles.currentPlanCard}>
        <div className={styles.planRow}>
          <Text className={styles.planLabel}>
            {SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.currentPlan}
          </Text>
          <Tag
            color={
              isTrialActive
                ? "default"
                : planData.plan === PLAN_TIERS.GALAXY
                ? "gold"
                : "green"
            }
          >
            {formatPlanLabel(planData.plan)}
          </Tag>
        </div>
        <div className={styles.planRow}>
          <Text className={styles.planLabel}>
            {SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.expiry}
          </Text>
          <Text className={styles.planValue}>
            {formatExpiry(planData.expiry)}
          </Text>
        </div>
      </div>

      {/* Action Buttons */}
      <Space direction="vertical" size="middle" className={styles.actionSpace}>
        <Button
          type="primary"
          block
          size="large"
          onClick={handleOpenRenew}
          className={styles.actionButton}
        >
          {isTrialActive
            ? SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.upgrade
            : SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.renewChange}
        </Button>
      </Space>
    </div>
  );

  const renewContent = (
    <div>
      {/* Only show back button if modal wasn't opened directly for payment */}
      {!initialPlan && (
        <Button
          type="link"
          onClick={() => setView("overview")}
          className={styles.backButton}
        >
          ← {SUBSCRIPTION_CONFIG.LABELS.RENEW.backButton}
        </Button>
      )}

      <Title level={5} className={styles.modalTitle}>
        {isTrialActive
          ? SUBSCRIPTION_CONFIG.LABELS.RENEW.title
          : SUBSCRIPTION_CONFIG.LABELS.RENEW.titleRenew}
      </Title>

      {!isTrialActive && (
        <Text type="secondary" className={styles.sectionLabel}>
          {SUBSCRIPTION_CONFIG.LABELS.RENEW.currentPlanNote.replace(
            "{plan}",
            formatPlanLabel(planData.plan)
          )}
        </Text>
      )}

      {/* Plan Selection */}
      <div className={styles.planRadioGroup}>
        <Text className={styles.sectionLabel}>
          {SUBSCRIPTION_CONFIG.LABELS.RENEW.planLabel}
        </Text>
        <Radio.Group
          value={renewPlan}
          onChange={(e) => setRenewPlan(e.target.value)}
          className={styles.radioGroup}
          size="large"
        >
          {PAID_PLANS.map((plan) => (
            <Radio.Button
              key={plan}
              value={plan}
            >
              {formatPlanLabel(plan)}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {/* Validity Selection */}
      <div className={styles.validityRadioGroup}>
        <Text className={styles.sectionLabel}>
          {SUBSCRIPTION_CONFIG.LABELS.RENEW.validityLabel}
        </Text>
        <Radio.Group
          value={renewValidity}
          onChange={(e) => setRenewValidity(e.target.value)}
          className={styles.radioGroup}
          size="large"
        >
          {VALIDITY_OPTIONS.map((opt) => (
            <Radio.Button key={opt.value} value={opt.value}>
              {opt.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {/* Pricing Details Display */}
      {currentPricing && (
        <div className={styles.pricingSection}>
          {/* Price Display */}
          <div className={styles.priceDisplay}>
            {currentPricing.hasOffer && (
              <div className={styles.priceDefaultStruck}>
                {formatCurrency(currentPricing.defaultMonthlyPrice)}/mo
              </div>
            )}
            <div className={styles.priceCurrent}>
              {formatCurrency(currentPricing.monthlyPrice)}
              <span className={styles.pricePeriod}>/mo</span>
            </div>
          </div>

          {/* Pay Today Section */}
          <div className={styles.payTodaySection}>
            <div className={styles.payTodayLabel}>Pay Today</div>
            <div className={styles.payTodayAmount}>
              {formatCurrency(currentPricing.finalTotal)}
            </div>
            {currentPricing.hasOffer &&
              currentPricing.originalTotal !== currentPricing.finalTotal && (
                <div className={styles.payTodayOriginal}>
                  {formatCurrency(currentPricing.originalTotal)}
                </div>
              )}
            {currentPricing.cycleDiscount > 0 && (
              <div className={styles.savingsNote}>
                Save {currentPricing.cycleDiscount}% with{" "}
                {currentPricing.cycleLabel.toLowerCase()} billing
              </div>
            )}
          </div>

          {/* Features List */}
          {PRICING_DATA[renewPlan]?.features && (
            <div className={styles.featuresSection}>
              <div className={styles.featuresTitle}>Included Features:</div>
              <ul className={styles.featuresList}>
                {PRICING_DATA[renewPlan].features.map((feature, idx) => (
                  <li key={idx} className={styles.featureItem}>
                    <span className={styles.featureCheckmark}>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Popconfirm
        title="Confirm Subscription"
        description={
          <>
            You are subscribing to <strong>{formatPlanLabel(renewPlan)}</strong>{" "}
            ({VALIDITY_OPTIONS.find((o) => o.value === renewValidity)?.label}).
            {currentPricing && (
              <>
                <br />
                <br />
                <strong>
                  Total: {formatCurrency(currentPricing.finalTotal)}
                </strong>
                <br />
                <span className={styles.popconfirmSmallText}>
                  You will be redirected to the payment gateway.
                </span>
              </>
            )}
          </>
        }
        onConfirm={handleRenewConfirm}
        okText="Proceed to Pay"
        cancelText="Cancel"
      >
        <Button
          type="primary"
          block
          size="large"
          loading={actionLoading}
          disabled={isBusy || !renewPlan}
          className={styles.actionButton}
        >
          {isTrialActive
            ? SUBSCRIPTION_CONFIG.LABELS.RENEW.subscribeAndPay
            : SUBSCRIPTION_CONFIG.LABELS.RENEW.renewAndPay}
        </Button>
      </Popconfirm>
    </div>
  );

  return (
    <Modal
      title={
        <span className={styles.modalTitle}>
          {view === "overview"
            ? SUBSCRIPTION_CONFIG.LABELS.OVERVIEW.title
            : isTrialActive
            ? SUBSCRIPTION_CONFIG.LABELS.RENEW.title
            : SUBSCRIPTION_CONFIG.LABELS.RENEW.titleRenew}
        </span>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={440}
      maskClosable={!isBusy}
      closable={!isBusy}
    >
      {view === "overview" && overviewContent}
      {view === "renew" && renewContent}
    </Modal>
  );
}
